/* eslint-disable import/prefer-default-export */
import { debounce } from 'lodash';

import { ordersListActions } from 'store/slices/ordersListSlice';
import {
  createReshipmentApi,
  getBatchShipmentsApi,
  getOrdersApi,
  updateOrderAssociationApi,
} from 'api/order';
import { applyFulfillingPharmacyFilter } from 'api/utils';
import { getRequestByReferenceIdApi } from 'api/orderStatus';
import { eventActions } from 'store/slices/eventSlice';
import { BASIC_ORDER_HISTORY_STATUSES } from 'common/constants/orderStatusGroups';
import { getPatientOrderHistory, listPatientOrders } from './patientThunks';
import { handleError } from './errorHandlerThunks';

export const getOrdersByPage =
  ({ page }) =>
  (dispatch, getState) => {
    const { sortBy, filter } = getState().ordersList;
    const updatedFilter = applyFulfillingPharmacyFilter(getState, filter);

    dispatch(ordersListActions.updateCurrentPage({ page }));

    return getOrdersApi({ page, sortBy, filter: updatedFilter })
      .then(({ count, orders }) => {
        dispatch(ordersListActions.loadOrdersToPage({ page, orders, count }));
      })
      .catch((error) => dispatch(handleError({ error })));
  };

export const debouncedGetOrders = debounce(({ dispatch }) => {
  return dispatch(getOrdersByPage({ page: 1 })).finally(() =>
    dispatch(ordersListActions.setOrdersListLoading(false))
  );
}, 1000);

export const sortOrdersList =
  ({ sortBy }) =>
  (dispatch) => {
    dispatch(ordersListActions.updateSortBy({ sortBy }));

    debouncedGetOrders({ dispatch });
  };

export const filterOrdersList =
  ({ filter }) =>
  (dispatch) => {
    dispatch(ordersListActions.setOrdersListLoading(true));
    dispatch(ordersListActions.updateFilter({ filter }));

    debouncedGetOrders({ dispatch });
  };

export const loadBatchShippedItems = ({ limit, offset, batchShipmentId }) => {
  return getBatchShipmentsApi({ limit, offset, batchShipmentId }).then((shipments) => {
    return shipments.response;
  });
};

export const createReshipment =
  ({
    input,
    mpi,
    patientAddressId,
    addressDirectoryId,
    isManuallyVerified,
    isValidationRequired,
    deliveryInstructions,
  }) =>
  (dispatch) => {
    return createReshipmentApi({
      input,
      patientAddressId,
      addressDirectoryId,
      isManuallyVerified,
      isValidationRequired,
      deliveryInstructions,
    })
      .then((createdReshipment) => {
        if (createdReshipment) {
          dispatch(listPatientOrders({ mpi }));
          dispatch(
            getPatientOrderHistory({
              mpi,
              status: BASIC_ORDER_HISTORY_STATUSES,
              includeAllFacilities: true,
            })
          );
        }

        return createdReshipment;
      })
      .catch((error) => dispatch(handleError({ error })));
  };

export const updateOrderAssociation =
  ({ patientId, ...payload }) =>
  (dispatch) => {
    return updateOrderAssociationApi({ patientId, ...payload })
      .then(() => {
        if (payload.includeAllFacilities) {
          return dispatch(
            getPatientOrderHistory({
              mpi: patientId,
              status: BASIC_ORDER_HISTORY_STATUSES,
              includeAllFacilities: true,
            })
          );
        }

        return dispatch(listPatientOrders({ mpi: patientId }));
      })
      .catch((error) => dispatch(handleError({ error })));
  };

export const getOrderEventHistory =
  ({ referenceId, orderId, requestId }) =>
  (dispatch) => {
    dispatch(eventActions.setEventLoading(true));
    return getRequestByReferenceIdApi({ referenceId, orderId, requestId })
      .then((response) => {
        dispatch(eventActions.setEventLoading(false));
        return response;
      })
      .catch((error) => {
        dispatch(eventActions.setEventLoading(false));
        dispatch(handleError({ error }));
      });
  };
